/** @jsxImportSource @emotion/react */

import { FC, useMemo } from 'react'
import { useMutation } from '@apollo/client'
import { createSearchParams } from 'react-router-dom'
import { FieldArray } from 'react-final-form-arrays'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { CREATE_DENTAL_SCHOOL_LOCATION } from 'queries/dentalSchools'
import {
  createDentalSchoolLocation,
  createDentalSchoolLocationVariables,
} from '__generatedTypes__/createDentalSchoolLocation'
import { QUERY_PARAM, SCHOOL_STATISTICS_TAB } from 'pagesNew/AdmissionManagement'
import { findAllDentalSchools_findAllDentalSchool_items as DentalSchool } from '__generatedTypes__/findAllDentalSchools'
import { validateRequiredField } from 'utils/validation'
import { toast } from 'utils/toast'
import { useBroadcastChannel } from 'hooks/useBroadcastChannel'
import { BROADCAST_CHANNEL } from 'constants/consts'
import { REFETCH_DENTAL_SCHOOLS_MESSAGE } from 'pagesNew/DentalSchools/Create'
import { Props as DentalSchoolsFieldArrayProps, DentalSchoolsFieldArray } from './Form/DentalSchoolsFieldArray'
import { Form, FormValues, getSchoolLocations } from './Form'

type Props = {
  platform: Platform
  isOptometry?: boolean
}

export const OrganizeCreateDentalSchoolStatistic: FC<Omit<Props, 'platform'>> = withPlatformCheck(
  ({ platform, isOptometry }: Props) => {
    const { navigate } = useLink()

    const [createDentalSchoolLocation] = useMutation<createDentalSchoolLocation, createDentalSchoolLocationVariables>(
      CREATE_DENTAL_SCHOOL_LOCATION
    )

    const handleSubmit = async (values: FormValues) => {
      try {
        const { title, description = '' } = values
        await createDentalSchoolLocation({
          variables: {
            createDentalSchoolLocationInput: {
              title,
              description,
              schoolLocationType: values.schoolLocation?.id,
              platform: {
                id: platform.id,
              },
              dentalSchool: values.schools.map(({ id }) => ({ id })),
              dentalSchoolIds: values.schools.map(({ id }) => id),
              isOptometry: !!isOptometry,
            },
          },
        })

        navigate(
          isOptometry ? cmsRoutes.OPTOMETRY : cmsRoutes.DENTISTRY,
          {},
          { searchParams: createSearchParams({ [QUERY_PARAM]: SCHOOL_STATISTICS_TAB }) }
        )
        toast.success(`${isOptometry ? 'Optometry' : 'Dental'} school statistics section created successfully`)
      } catch (error) {
        toast.error(`Failed to create ${isOptometry ? 'optometry' : 'dental'} school statistics section`)
      }
    }

    useBroadcastChannel(BROADCAST_CHANNEL, (event) => {
      if (event.data === REFETCH_DENTAL_SCHOOLS_MESSAGE) {
        toast.success(`${isOptometry ? 'Optometry' : 'Dental'} school created successfully`)
      }
    })

    const initialValues: FormValues = useMemo(() => {
      const schoolLocations = getSchoolLocations(isOptometry)

      return {
        title: '',
        description: '',
        schoolLocation: schoolLocations[0],
        schools: [],
      }
    }, [isOptometry])

    return (
      <Form
        platform={platform}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        title={`Organize ${isOptometry ? 'Optometry' : 'Dental'} School Statistics`}
        createLink={isOptometry ? cmsRoutes.CREATE_OPTOMETRY_SCHOOL : cmsRoutes.CREATE_DENTAL_SCHOOL}
        renderFields={({ form, linkParams }) => (
          <FieldArray<DentalSchool> name="schools" validate={validateRequiredField}>
            {({ fields, meta }) => (
              <DentalSchoolsFieldArray
                platform={platform}
                isOptometry={!!isOptometry}
                onChange={form.change as unknown as DentalSchoolsFieldArrayProps['onChange']}
                fields={fields}
                meta={meta}
                linkParams={linkParams}
              />
            )}
          </FieldArray>
        )}
        isOptometry={isOptometry}
      />
    )
  }
)
